import { Box } from '@shared/components';
import { useRef } from 'react';

import { useRichTextEditor } from '../../providers';
import { PhotoIcon } from '../icons';

import ControlButton from './control-button';

export default function RichTextEditorImageControl() {
  const { editor, onImageUpload } = useRichTextEditor();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current === null) {
      return null;
    }
    fileInputRef.current.click();
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (editor === null) {
      return null;
    }

    if (!(file instanceof File)) {
      return null;
    }

    // Reset the input value to allow uploading the same file again
    // Previously, the input value was not reset, so the same file could not be uploaded again
    event.target.value = '';

    if (typeof onImageUpload !== 'undefined') {
      const response = await onImageUpload(file);

      if (response === undefined) {
        return null;
      }

      const { url, fileName } = response;

      if (url !== undefined) {
        editor.chain().focus().setCustomImage({ src: url, content: fileName }).run();
      }
    } else {
      const url = URL.createObjectURL(file);

      editor.chain().focus().setImage({ src: url }).run();

      // Clean up the URL when the component unmounts or the file changes
      editor.on('destroy', () => URL.revokeObjectURL(url));
    }
  };

  return (
    <>
      <ControlButton
        aria-label='Upload Image'
        data-rich-text-editor-control
        onClick={handleButtonClick}
      >
        <PhotoIcon />
        <Box className='sr-only' component='span' sx={{ display: 'none' }}>
          Upload Image
        </Box>
      </ControlButton>
      {/* We use HTML native input because we use it to upload images without displaying it */}
      <input
        type='file'
        accept='.jpg,.jpeg,.png'
        ref={fileInputRef}
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
    </>
  );
}
